/**
 * columnFilters — types + logic cho filter per-column (Excel-style).
 *
 * Mỗi column trên header có 1 icon ▽; click mở popover filter riêng. Kind của
 * popover tự detect theo `ColumnDef.kind` (date/datetime/number/percent) hoặc
 * theo distinct values trong baseRows (≤ CHECKBOX_THRESHOLD → checkboxes,
 * còn lại → text). Page có thể override qua `ColumnDef.filter`.
 *
 * Filter Bar global "Search" và filter per-column AND nhau:
 *   rowMatchesSearch(row, term, columns) && rowMatchesAllColumnFilters(row, filters)
 */
import dayjs from 'dayjs'

export type ColumnFilterKind = 'text' | 'checkboxes' | 'date' | 'number' | 'none'

export type ColumnFilterValue =
  | { kind: 'text'; value: string }
  | { kind: 'checkboxes'; selected: string[] }
  | { kind: 'date'; from: string | null; to: string | null }
  | { kind: 'number'; min: number | null; max: number | null }

/** Subset của ColumnDef mà template cần — page tự extend. */
export interface ColumnLike {
  key: string
  label?: string
  /** Render kind từ codebase (text/date/datetime/number/percent...). */
  kind?: string
  /** Override auto-detect filter kind. */
  filter?: ColumnFilterKind
}

export type ColumnFiltersState = Record<string, ColumnFilterValue | undefined>

/** Cột chưa khai báo `filter` mà distinct values ≤ ngưỡng này → checkboxes. */
export const CHECKBOX_THRESHOLD = 30

/**
 * Detect filter kind cho 1 column. Ưu tiên:
 *   1. column.filter override (nếu page khai báo)
 *   2. column.kind === 'date' | 'datetime' → 'date'
 *   3. column.kind === 'number' | 'percent' | 'currency' → 'number'
 *   4. Đếm distinct values trong baseRows: ≤ CHECKBOX_THRESHOLD → 'checkboxes', else 'text'
 */
export function inferColumnFilterKind<R extends Record<string, any>>(
  column: ColumnLike,
  baseRows: readonly R[],
): ColumnFilterKind {
  if (column.filter) return column.filter
  if (column.kind === 'date' || column.kind === 'datetime') return 'date'
  if (column.kind === 'number' || column.kind === 'percent' || column.kind === 'currency') return 'number'

  const seen = new Set<string>()
  for (const row of baseRows) {
    const v = (row as Record<string, unknown>)[column.key]
    if (v == null) continue
    seen.add(String(v))
    if (seen.size > CHECKBOX_THRESHOLD) return 'text'
  }
  return 'checkboxes'
}

export function isFilterActive(value: ColumnFilterValue | undefined): boolean {
  if (!value) return false
  switch (value.kind) {
    case 'text':
      return value.value.trim() !== ''
    case 'checkboxes':
      return value.selected.length > 0
    case 'date':
      return value.from != null || value.to != null
    case 'number':
      return value.min != null || value.max != null
  }
}

export function rowMatchesColumnFilter<R extends Record<string, any>>(
  row: R,
  columnKey: string,
  value: ColumnFilterValue | undefined,
): boolean {
  if (!isFilterActive(value) || !value) return true
  const raw = (row as Record<string, unknown>)[columnKey]
  switch (value.kind) {
    case 'text': {
      if (raw == null) return false
      return String(raw).toLowerCase().includes(value.value.trim().toLowerCase())
    }
    case 'checkboxes': {
      if (raw == null) return false
      return value.selected.includes(String(raw))
    }
    case 'date': {
      if (raw == null) return false
      const d = dayjs(String(raw))
      if (!d.isValid()) return false
      if (value.from && d.isBefore(dayjs(value.from), 'day')) return false
      if (value.to && d.isAfter(dayjs(value.to), 'day')) return false
      return true
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(raw)
      if (!Number.isFinite(n)) return false
      if (value.min != null && n < value.min) return false
      if (value.max != null && n > value.max) return false
      return true
    }
  }
}

export function rowMatchesAllColumnFilters<R extends Record<string, any>>(
  row: R,
  filters: ColumnFiltersState,
): boolean {
  for (const key of Object.keys(filters)) {
    if (!rowMatchesColumnFilter(row, key, filters[key])) return false
  }
  return true
}

/** Distinct values cho checkbox option list — sorted A→Z, bỏ blank. */
export function distinctColumnValues<R extends Record<string, any>>(
  rows: readonly R[],
  columnKey: string,
): string[] {
  const seen = new Set<string>()
  for (const row of rows) {
    const v = (row as Record<string, unknown>)[columnKey]
    if (v == null) continue
    const t = String(v).trim()
    if (t) seen.add(t)
  }
  return [...seen].sort((a, b) => a.localeCompare(b))
}
